import React from 'react';
import { Separator, Button, Card } from '@roger/ui-components';
import { Edit, X, CheckCheck } from 'lucide-react';
import { parse, format, isValid } from 'date-fns';
import ClickableTextWithModals from './ClickableTextWithModals';
import DiagnosisFieldsEditor from './DiagnosisFieldsEditor';
import { useStore } from '../store';
import { FeatureFlag } from '../constants/featureFlags';
import type {
  DiagnosisWithReasoning,
  PrimaryDiagnosis,
  OEType,
  SeverityType,
} from '../types/codingData.types';

// Helper to parse MM/DD/YYYY string to Date (API format -> Date)
const parseOnsetDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  const parsed = parse(dateStr, 'MM/dd/yyyy', new Date());
  return isValid(parsed) ? parsed : null;
};

// Helper to format Date to MM/DD/YYYY string (Date -> API format)
const formatOnsetDate = (date: Date | null): string | null => {
  if (!date) return null;
  return format(date, 'MM/dd/yyyy');
};

interface DiagnosisCardProps {
  diagnosis: DiagnosisWithReasoning | PrimaryDiagnosis;
  variant: 'primary' | 'secondary';
  onOpenModal?: (dataType: string) => void;
  position?: number; // Position number for secondary diagnoses
  onEdit?: () => void;
  onRemove?: () => void;
  isCompliant?: boolean; // For primary diagnosis: matches physician-authorized diagnoses
  onFieldChange?: (fields: {
    oe?: OEType;
    onset_date?: string | null;
    severity?: SeverityType;
  }) => void;
}

const DiagnosisCard: React.FC<DiagnosisCardProps> = ({
  diagnosis,
  variant,
  onOpenModal,
  position,
  onEdit,
  onRemove,
  isCompliant,
  onFieldChange,
}) => {
  const isPrimary = variant === 'primary';
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const showOEDateSeverityFields = hasFeatureFlag(
    FeatureFlag.MEDICAL_CODING_OE_DATE_SEVERITY_UI
  );

  // Handle field changes
  const handleOEChange = (value: OEType) => {
    onFieldChange?.({
      oe: value,
      onset_date: diagnosis.diagnosis.onset_date,
      severity: diagnosis.diagnosis.severity,
    });
  };

  const handleOnsetDateChange = (value: Date | null) => {
    onFieldChange?.({
      oe: diagnosis.diagnosis.oe,
      onset_date: formatOnsetDate(value),
      severity: diagnosis.diagnosis.severity,
    });
  };

  const handleSeverityChange = (value: SeverityType) => {
    onFieldChange?.({
      oe: diagnosis.diagnosis.oe,
      onset_date: diagnosis.diagnosis.onset_date,
      severity: value,
    });
  };

  if (isPrimary) {
    return (
      <>
        {/* Code and Description */}
        <div className='flex items-center gap-4 mb-4'>
          {/* Code Badge */}
          <div className='px-4 py-2 rounded-lg bg-primary border border-primary/20'>
            <span className='text-white font-black text-xl font-mono tracking-wider'>
              {diagnosis.diagnosis.name}
            </span>
          </div>

          {/* Description */}
          <div className='flex-1'>
            <p className='text-xl font-semibold text-gray-700 leading-tight'>
              {diagnosis.diagnosis.desc}
            </p>
          </div>

          {/* Compliance Status Badge */}
          {isCompliant && (
            <div className='flex items-center gap-2'>
              <CheckCheck className='w-3 h-3 text-green-700' />
              <span className='text-xs font-semibold text-green-700 leading-tight'>
                Matches Physician-Authorized Diagnoses
              </span>
            </div>
          )}

          {/* Edit Button */}
          {onEdit && (
            <Button
              variant='outline'
              size='sm'
              onClick={onEdit}
              className='shrink-0'
            >
              <Edit className='w-4 h-4 mr-2' />
              Edit
            </Button>
          )}
        </div>

        {/* O/E, Date, Severity Fields */}
        {showOEDateSeverityFields && (
          <div className='mb-4'>
            <DiagnosisFieldsEditor
              oe={diagnosis.diagnosis.oe}
              onsetDate={parseOnsetDate(diagnosis.diagnosis.onset_date)}
              onsetDateRaw={diagnosis.diagnosis.onset_date}
              severity={diagnosis.diagnosis.severity}
              onOEChange={handleOEChange}
              onOnsetDateChange={handleOnsetDateChange}
              onSeverityChange={handleSeverityChange}
              variant='full'
            />
          </div>
        )}

        {/* Reasoning Section */}
        {diagnosis.reasoning && (
          <>
            <div className='text-sm text-gray-700 leading-relaxed'>
              {onOpenModal ? (
                <ClickableTextWithModals
                  text={diagnosis.reasoning}
                  onOpenModal={onOpenModal}
                  size='sm'
                  className='text-gray-700'
                />
              ) : (
                <div className='whitespace-pre-wrap'>{diagnosis.reasoning}</div>
              )}
            </div>
          </>
        )}
      </>
    );
  }

  // Secondary diagnosis card
  return (
    <Card className='h-full'>
      {/* Header with Position Badge and Actions */}
      <div className='flex items-center justify-between mb-2'>
        {/* Position Badge */}
        {position && (
          <div className='text-gray-500 text-xs font-semibold'>#{position}</div>
        )}

        {/* Action Buttons */}
        {(onEdit || onRemove) && (
          <div className='flex gap-1'>
            {onEdit && (
              <Button
                variant='ghost'
                size='sm'
                onClick={onEdit}
                className='h-6 w-6 p-0'
              >
                <Edit className='w-3 h-3' />
              </Button>
            )}
            {onRemove && (
              <Button
                variant='ghost'
                size='sm'
                onClick={onRemove}
                className='h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50'
              >
                <X className='w-3 h-3' />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Code and Description */}
      <div className='flex items-center gap-2 mb-2'>
        {/* Code Badge */}
        <div className='px-2 rounded bg-primary border border-primary/20'>
          <span className='text-white font-bold text-xs font-mono tracking-wide'>
            {diagnosis.diagnosis.name}
          </span>
        </div>

        {/* Description */}
        <p className='text-sm font-medium text-gray-700 leading-tight flex-1'>
          {diagnosis.diagnosis.desc}
        </p>
      </div>

      {/* O/E, Date, Severity Fields */}
      {showOEDateSeverityFields && (
        <div className='mb-3'>
          <DiagnosisFieldsEditor
            oe={diagnosis.diagnosis.oe}
            onsetDate={parseOnsetDate(diagnosis.diagnosis.onset_date)}
            onsetDateRaw={diagnosis.diagnosis.onset_date}
            severity={diagnosis.diagnosis.severity}
            onOEChange={handleOEChange}
            onOnsetDateChange={handleOnsetDateChange}
            onSeverityChange={handleSeverityChange}
            variant='compact'
          />
        </div>
      )}

      {/* Reasoning Section */}
      {diagnosis.reasoning && (
        <>
          <Separator className='my-4' />
          <div className='text-xs text-gray-700 leading-relaxed'>
            {onOpenModal ? (
              <ClickableTextWithModals
                text={diagnosis.reasoning}
                onOpenModal={onOpenModal}
                size='xs'
                className='text-gray-700'
              />
            ) : (
              <div className='whitespace-pre-wrap'>{diagnosis.reasoning}</div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default DiagnosisCard;
